import { useState } from "react";
import {
    Box, Typography, Grid, Avatar, Chip, useMediaQuery, Tabs, Tab
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { AiFillBulb } from "react-icons/ai";

const PageWrapper = styled(Box)(() => ({
    minHeight: "100vh",
    background: "linear-gradient(160deg, #03020a 0%, #060c18 50%, #03050f 100%)",
    color: "#fff",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    paddingBottom: "60px",
    paddingTop: "32px",
}));

const SectionCard = styled(Box)(() => ({
    background: "rgba(26,14,52,0.95)",
    border: "1px solid rgba(218,165,32,0.25)",
    borderRadius: "18px",
    padding: "32px",
    marginBottom: "28px",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: "10%", right: "10%", height: "1px",
        background: "linear-gradient(to right,transparent,rgba(218,165,32,0.3),transparent)",
    },
}));

const MobileDataCard = styled(Box)(() => ({
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(218,165,32,0.15)",
    borderRadius: "12px",
    padding: "14px 16px",
    marginBottom: "10px",
}));

const CodeBlock = styled(Box)(() => ({
    background: "rgba(6,2,16,0.98)",
    border: "1px solid rgba(218,165,32,0.2)",
    borderRadius: "12px",
    padding: "20px 24px",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    fontSize: "0.82rem",
    lineHeight: 1.9,
    overflowX: "auto",
    whiteSpace: "pre",
    color: "#e6edf3",
    position: "relative",
    "&::before": {
        content: '"C++"',
        position: "absolute",
        top: "8px", right: "14px",
        fontSize: "9px",
        color: "rgba(218,165,32,0.5)",
        letterSpacing: "2px",
        fontWeight: 700,
    },
}));

const TableHeader = ({ columns, cols }) => (
    <Box sx={{
        display: "grid",
        gridTemplateColumns: cols || `repeat(${columns.length},1fr)`,
        gap: "12px", padding: "12px 16px",
        bgcolor: "rgba(218,165,32,0.1)", color: "goldenrod",
        fontWeight: "bold", fontSize: "0.78rem",
        letterSpacing: "0.06em", textTransform: "uppercase",
        borderBottom: "1px solid rgba(218,165,32,0.15)",
    }}>
        {columns.map(c => <div key={c}>{c}</div>)}
    </Box>
);

const SectionHeading = ({ icon, title, subtitle }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 4, mb: subtitle ? 1 : 2 }}>
        <Avatar sx={{ bgcolor: "goldenrod", color: "#100720", width: 38, height: 38, flexShrink: 0, mt: 0.3 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{subtitle}</Typography>}
        </Box>
    </Box>
);

const ConceptCard = ({ title, color = "goldenrod", children }) => (
    <Box sx={{
        bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`, borderRadius: "12px",
        p: "16px 18px", mb: 2,
    }}>
        {title && <Typography sx={{ fontWeight: 700, color, fontSize: "0.82rem", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</Typography>}
        {children}
    </Box>
);

const StyledTabs = ({ value, onChange, tabs }) => (
    <Tabs value={value} onChange={onChange} variant="scrollable" scrollButtons="auto"
          sx={{
              mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)",
              "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "none", minHeight: 40 },
              "& .Mui-selected": { color: "goldenrod !important" },
              "& .MuiTabs-indicator": { bgcolor: "goldenrod" },
          }}>
        {tabs.map((t, i) => <Tab key={i} label={t} />)}
    </Tabs>
);

const PillChip = ({ label, color = "goldenrod" }) => (
    <Chip label={label} size="small" sx={{
        bgcolor: `${color}18`, color, fontWeight: 700, fontSize: "0.72rem",
        height: 22, border: `1px solid ${color}44`, "& .MuiChip-label": { px: 1 },
    }} />
);
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 1: Stream Classes & Opening Files
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const openTabs = [
    {
        label: "ifstream / ofstream",
        code: `#include <fstream>
#include <iostream>
#include <string>

// ── Three file stream classes ─────────────────────────────────
//   std::ifstream  — input  (read from file)
//   std::ofstream  — output (write to file)
//   std::fstream   — both   (read + write)

// ── Opening a file for READING ────────────────────────────────
std::ifstream inFile("data.txt");       // opens immediately in constructor

if (!inFile.is_open()) {                // ALWAYS check before reading!
    std::cerr << "Error: cannot open data.txt\\n";
    return 1;
}

// ── Opening a file for WRITING ────────────────────────────────
std::ofstream outFile("output.txt");    // creates file if not exists
                                        // TRUNCATES (erases) if exists!
if (!outFile) {                         // stream converts to bool
    std::cerr << "Error: cannot open output.txt\\n";
    return 1;
}

// ── RAII: files close automatically when stream goes out of scope ─
{
    std::ifstream f("config.txt");      // opened
    // ... read from f ...
}   // ← f.close() called automatically here — no need to call manually

// ── Explicit open/close ───────────────────────────────────────
std::ifstream f2;                       // default constructed — no file
f2.open("another.txt");                 // open separately
// ... use f2 ...
f2.close();                             // explicit close (optional with RAII)
f2.open("next.txt");                    // can reuse after close`,
    },
    {
        label: "Open Modes",
        code: `#include <fstream>
#include <ios>

// ── File open modes — pass as second argument ─────────────────
//   std::ios::in      read (default for ifstream)
//   std::ios::out     write, truncate (default for ofstream)
//   std::ios::app     append — always write to end
//   std::ios::ate     seek to end on open (but can seek elsewhere)
//   std::ios::trunc   truncate file to zero on open
//   std::ios::binary  binary mode — no newline translation

// ── Common mode combinations ──────────────────────────────────
// Read only (default for ifstream):
std::ifstream f1("file.txt");
std::ifstream f2("file.txt", std::ios::in);      // same thing

// Write, truncate existing content:
std::ofstream f3("out.txt");
std::ofstream f4("out.txt", std::ios::out | std::ios::trunc); // same

// Append — DON'T erase existing content:
std::ofstream f5("log.txt", std::ios::app);      // write to end always

// Read + write without truncating:
std::fstream  f6("data.txt", std::ios::in | std::ios::out);

// Binary read:
std::ifstream f7("image.png", std::ios::in | std::ios::binary);

// Binary write:
std::ofstream f8("out.bin", std::ios::out | std::ios::binary);

// Read + write in binary:
std::fstream  f9("db.bin",
    std::ios::in | std::ios::out | std::ios::binary);

// Create new or truncate (equivalent to "w" in C fopen):
std::ofstream f10("new.txt", std::ios::out | std::ios::trunc);`,
    },
    {
        label: "Error Checking",
        code: `#include <fstream>
#include <iostream>
#include <stdexcept>

// ── Stream state flags ────────────────────────────────────────
//   good()  — all fine, no errors
//   eof()   — end of file reached
//   fail()  — logical error (wrong format, e.g. reading int from "abc")
//   bad()   — I/O or hardware error (severe)

std::ifstream f("data.txt");

// ── Method 1: bool conversion (most common) ───────────────────
if (!f) {
    std::cerr << "Failed to open file\\n";
}

// ── Method 2: is_open() ───────────────────────────────────────
if (!f.is_open()) {
    std::cerr << "File not open\\n";
}

// ── Checking after each read ──────────────────────────────────
int value;
while (f >> value) {          // loop exits when read fails (EOF or error)
    std::cout << value << ' ';
}

if (f.eof())  std::cout << "Reached end of file\\n";
if (f.fail()) std::cerr << "Read failed (format error?)\\n";
if (f.bad())  std::cerr << "I/O error\\n";

// ── exceptions() — throw on stream errors ─────────────────────
std::ifstream f2("data.txt");
f2.exceptions(std::ios::failbit | std::ios::badbit); // throw on fail or bad
try {
    int n; f2 >> n;           // throws std::ios_base::failure on error
}
catch (const std::ios_base::failure& e) {
    std::cerr << "Stream error: " << e.what() << "\\n";
}

// ── clear() — reset error flags to continue using stream ───────
f.clear();                    // must call before more operations after error
f.seekg(0);                   // go back to start`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 2: Reading Files
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const readTabs = [
    {
        label: "Read Line by Line",
        code: `#include <fstream>
#include <string>
#include <iostream>
#include <vector>

// ── std::getline — reads a full line including spaces ─────────
std::ifstream file("poem.txt");
std::string line;

while (std::getline(file, line)) {    // reads until '\\n' or EOF
    std::cout << line << '\\n';
    // line does NOT contain the '\\n' character
}
// Loop exits when getline reaches EOF (returns false)

// ── Store all lines in a vector ───────────────────────────────
std::vector<std::string> readAllLines(const std::string& path) {
    std::ifstream file(path);
    if (!file) throw std::runtime_error("Cannot open: " + path);

    std::vector<std::string> lines;
    std::string line;
    while (std::getline(file, line))
        lines.push_back(std::move(line));  // move avoids copy
    return lines;
}

// ── Line count ────────────────────────────────────────────────
int countLines(const std::string& path) {
    std::ifstream file(path);
    int count = 0;
    std::string line;
    while (std::getline(file, line)) ++count;
    return count;
}

// ── Read until specific delimiter ────────────────────────────
std::string token;
while (std::getline(file, token, ',')) {  // split on comma
    std::cout << "Token: " << token << '\\n';
}`,
    },
    {
        label: "Read Word / Token",
        code: `#include <fstream>
#include <string>
#include <sstream>
#include <vector>

// ── operator>> skips whitespace, reads until next whitespace ──
std::ifstream file("numbers.txt");
int n;
while (file >> n) {                // reads ints separated by whitespace
    std::cout << n << ' ';
}

// ── Mixed types ───────────────────────────────────────────────
// File contains: "Alice 25 3.8\\nBob 30 3.2"
struct Student { std::string name; int age; double gpa; };

std::vector<Student> students;
std::ifstream f("students.txt");
Student s;
while (f >> s.name >> s.age >> s.gpa) {
    students.push_back(s);
}

// ── Parse CSV line ─────────────────────────────────────────────
// "Alice,25,Engineering" → extract each field
std::string csvLine = "Alice,25,Engineering";
std::istringstream ss(csvLine);
std::string field;
while (std::getline(ss, field, ','))    // split string on ','
    std::cout << field << '\\n';

// ── Read entire file into string ──────────────────────────────
std::string readAllText(const std::string& path) {
    std::ifstream file(path);
    if (!file) throw std::runtime_error("Cannot open: " + path);
    // Iterator constructor — reads entire file at once
    return std::string(
        std::istreambuf_iterator<char>(file),
        std::istreambuf_iterator<char>()
    );
}`,
    },
    {
        label: "Read Structured Data",
        code: `#include <fstream>
#include <sstream>
#include <vector>
#include <string>

// ── Read CSV file ─────────────────────────────────────────────
// File: name,age,score
//       Alice,25,98.5
//       Bob,30,87.2

struct Record { std::string name; int age; double score; };

std::vector<Record> parseCSV(const std::string& path) {
    std::ifstream file(path);
    if (!file) throw std::runtime_error("Cannot open: " + path);

    std::vector<Record> records;
    std::string line;

    std::getline(file, line); // skip header row

    while (std::getline(file, line)) {
        if (line.empty()) continue;         // skip blank lines

        std::istringstream ss(line);
        Record r;
        std::string ageStr, scoreStr;

        std::getline(ss, r.name,    ',');   // read name
        std::getline(ss, ageStr,    ',');   // read age as string
        std::getline(ss, scoreStr       );  // read score (rest of line)

        r.age   = std::stoi(ageStr);
        r.score = std::stod(scoreStr);
        records.push_back(r);
    }
    return records;
}

// ── Read key=value config file ────────────────────────────────
// File: host=localhost\\nport=8080\\ndebug=true
#include <map>
std::map<std::string, std::string> parseConfig(const std::string& path) {
    std::ifstream file(path);
    std::map<std::string,std::string> cfg;
    std::string line;
    while (std::getline(file, line)) {
        if (line.empty() || line[0] == '#') continue;  // skip comments
        auto eq = line.find('=');
        if (eq != std::string::npos)
            cfg[line.substr(0, eq)] = line.substr(eq + 1);
    }
    return cfg;
}`,
    },
    {
        label: "Read Entire File",
        code: `#include <fstream>
#include <sstream>
#include <string>
#include <iterator>

// ── Method 1: istreambuf_iterator (fastest) ───────────────────
std::string readFile1(const std::string& path) {
    std::ifstream f(path, std::ios::binary);   // binary to preserve \\r\\n
    if (!f) throw std::runtime_error("Cannot open: " + path);
    return { std::istreambuf_iterator<char>(f),
             std::istreambuf_iterator<char>() };
}

// ── Method 2: stringstream rdbuf (concise) ────────────────────
std::string readFile2(const std::string& path) {
    std::ifstream f(path);
    if (!f) throw std::runtime_error("Cannot open: " + path);
    std::ostringstream ss;
    ss << f.rdbuf();            // read entire stream buffer at once
    return ss.str();
}

// ── Method 3: seekg to get size, then read ────────────────────
std::string readFile3(const std::string& path) {
    std::ifstream f(path, std::ios::binary | std::ios::ate);
    if (!f) throw std::runtime_error("Cannot open: " + path);

    auto size = f.tellg();      // file size (we opened at end with ate)
    f.seekg(0);                 // seek back to start

    std::string buffer(size, '\\0');
    f.read(buffer.data(), size); // read all at once into pre-sized string
    return buffer;
}

// ── Which method to use? ──────────────────────────────────────
//  Small text files:   method 2 (clean and readable)
//  Large files:        method 3 (pre-allocate, single read)
//  Binary files:       method 1 or 3 with std::ios::binary`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 3: Writing Files
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const writeTabs = [
    {
        label: "Basic Writing",
        code: `#include <fstream>
#include <iomanip>   // for formatting

// ── Write text with operator<< ────────────────────────────────
std::ofstream out("report.txt");
if (!out) throw std::runtime_error("Cannot create report.txt");

out << "Name: Alice\\n";
out << "Age:  30\\n";
out << "GPA:  " << std::fixed << std::setprecision(2) << 3.85 << "\\n";

// ── Write a line ──────────────────────────────────────────────
std::string line = "Hello, World!";
out << line << "\\n";         // operator<< + newline
out.put('\\n');               // write single character

// ── write() — write raw bytes (text or binary) ────────────────
const char* msg = "Raw bytes\\n";
out.write(msg, strlen(msg));  // writes exactly n bytes, no null terminator

// ── flush() — force buffer to disk ───────────────────────────
out << "Important data" << std::flush;   // flush inline with <<
out.flush();                             // explicit flush
// Note: file is flushed and closed automatically when ofstream is destroyed

// ── Append to existing file ───────────────────────────────────
std::ofstream log("app.log", std::ios::app);
log << "[2024-01-15 10:30] Application started\\n";
log << "[2024-01-15 10:31] User logged in\\n";
// Each run appends — does NOT truncate!

// ── Check write succeeded ────────────────────────────────────
out << "data";
if (!out) {
    std::cerr << "Write failed! Disk full?\\n";
}`,
    },
    {
        label: "Write Structured Data",
        code: `#include <fstream>
#include <vector>
#include <iomanip>

struct Product {
    std::string name;
    double price;
    int    stock;
};

// ── Write CSV file ─────────────────────────────────────────────
void writeCSV(const std::string& path,
              const std::vector<Product>& products) {
    std::ofstream file(path);
    if (!file) throw std::runtime_error("Cannot create: " + path);

    // Header row
    file << "name,price,stock\\n";

    for (const auto& p : products) {
        file << p.name  << ','
             << std::fixed << std::setprecision(2) << p.price << ','
             << p.stock << '\\n';
    }
}

// ── Write formatted table ─────────────────────────────────────
void writeTable(std::ostream& out,        // takes any ostream!
                const std::vector<Product>& products) {
    out << std::left                      // left-align text
        << std::setw(20) << "Product"
        << std::right
        << std::setw(10) << "Price"
        << std::setw(10) << "Stock" << "\\n";
    out << std::string(40, '-') << "\\n";

    for (const auto& p : products) {
        out << std::left  << std::setw(20) << p.name
            << std::right << std::setw(10) << std::fixed
            << std::setprecision(2) << p.price
            << std::setw(10) << p.stock << "\\n";
    }
}

// ── Write to file AND console simultaneously ──────────────────
std::ofstream logFile("log.txt");
writeTable(logFile,  products);   // write to file
writeTable(std::cout, products);  // write same thing to console`,
    },
    {
        label: "Atomic Write (Safe)",
        code: `#include <fstream>
#include <filesystem>
#include <stdexcept>

// ── Atomic write pattern: write to temp, then rename ──────────
// PROBLEM: if program crashes mid-write, file is left corrupted
// SOLUTION: write to a temp file, then atomically rename

void atomicWrite(const std::string& targetPath,
                 const std::string& content) {
    std::string tempPath = targetPath + ".tmp";

    // Step 1: Write to temp file
    {
        std::ofstream temp(tempPath);
        if (!temp) throw std::runtime_error("Cannot create temp file");
        temp << content;
        if (!temp) throw std::runtime_error("Write failed");
        // temp is flushed and closed here (RAII destructor)
    }

    // Step 2: Rename temp → target (atomic on most filesystems)
    std::filesystem::rename(tempPath, targetPath);
    // If rename fails it throws filesystem_error — temp file still exists
}

// ── Backup existing file before overwriting ────────────────────
void safeOverwrite(const std::string& path, const std::string& content) {
    namespace fs = std::filesystem;
    std::string backup = path + ".bak";

    // Backup current file if it exists
    if (fs::exists(path))
        fs::copy_file(path, backup, fs::copy_options::overwrite_existing);

    try {
        atomicWrite(path, content);
        fs::remove(backup);          // success — remove backup
    }
    catch (...) {
        // Restore backup on failure
        if (fs::exists(backup))
            fs::rename(backup, path);
        throw;
    }
}`,
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 4: Random Access & seekg/seekp
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const seekTabs = [
    {
        label: "seekg / seekp / tellg",
        code: `#include <fstream>
#include <iostream>

// ── File position pointers ────────────────────────────────────
//   seekg(pos, origin)  — seek GET pointer (read position)
//   seekp(pos, origin)  — seek PUT pointer (write position)
//   tellg()             — current GET position (byte offset)
//   tellp()             — current PUT position
//
//   Origin:
//     std::ios::beg  — from beginning of file
//     std::ios::cur  — from current position
//     std::ios::end  — from end of file

std::fstream file("data.txt", std::ios::in | std::ios::out);

// ── Get file size ─────────────────────────────────────────────
file.seekg(0, std::ios::end);   // seek to end
auto fileSize = file.tellg();   // get position = size in bytes
file.seekg(0, std::ios::beg);   // back to start
std::cout << "File size: " << fileSize << " bytes\\n";

// ── Random access reads ───────────────────────────────────────
file.seekg(10);                 // jump to byte 10
char c; file.get(c);            // read 1 byte from offset 10

file.seekg(-5, std::ios::end);  // 5 bytes before end
std::string last5(5, '\\0');
file.read(last5.data(), 5);     // read last 5 bytes

// ── Relative seeking ─────────────────────────────────────────
auto pos = file.tellg();        // save current position
file.seekg(20, std::ios::cur);  // skip forward 20 bytes
// ... read something ...
file.seekg(pos);                // restore saved position

// ── Write at specific position ────────────────────────────────
file.seekp(0);                  // go to beginning
file << "UPDATED";              // overwrite first 7 bytes

file.seekp(0, std::ios::end);   // go to end
file << "\\nNew line appended";  // append`,
    },
    {
        label: "Fixed-Length Records",
        code: `#include <fstream>
#include <cstring>
#include <iostream>

// ── Fixed-length records allow O(1) random access ─────────────
// Each record occupies exactly the same number of bytes

struct EmployeeRecord {
    char   name[32];     // fixed-size char array (not std::string!)
    int    id;
    double salary;
    bool   active;
    char   padding[3];   // explicit padding to control alignment
};
// sizeof(EmployeeRecord) is always constant → enables seeking

void writeRecord(std::fstream& f, int index, const EmployeeRecord& emp) {
    auto offset = index * sizeof(EmployeeRecord);
    f.seekp(offset, std::ios::beg);
    f.write(reinterpret_cast<const char*>(&emp), sizeof(emp));
}

EmployeeRecord readRecord(std::fstream& f, int index) {
    EmployeeRecord emp{};
    auto offset = index * sizeof(EmployeeRecord);
    f.seekg(offset, std::ios::beg);
    f.read(reinterpret_cast<char*>(&emp), sizeof(emp));
    return emp;
}

int getRecordCount(std::fstream& f) {
    f.seekg(0, std::ios::end);
    return static_cast<int>(f.tellg() / sizeof(EmployeeRecord));
}

int main() {
    std::fstream db("employees.db",
        std::ios::in | std::ios::out | std::ios::binary);

    // Write record at index 5:
    EmployeeRecord emp{};
    strncpy(emp.name, "Alice", sizeof(emp.name)-1);
    emp.id = 1001; emp.salary = 75000.0; emp.active = true;
    writeRecord(db, 5, emp);

    // Read it back:
    auto rec = readRecord(db, 5);
    std::cout << "Name: " << rec.name << ", ID: " << rec.id << "\\n";
}`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 5: Binary Files
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const binaryTabs = [
    {
        label: "Binary Read / Write",
        code: `#include <fstream>
#include <cstdint>

// ── Text vs Binary mode ───────────────────────────────────────
//  Text mode:   '\\n' may be translated (\\r\\n on Windows)
//               Numbers written as characters: 12345 → "12345" (5 bytes)
//  Binary mode: exact bytes, no translation
//               Numbers as raw bytes: 12345 → 4 bytes (int representation)
//
//  Use binary for: images, audio, databases, serialised data, protocols

struct Vec3 {
    float x, y, z;
};

// ── Write binary ──────────────────────────────────────────────
void writeBinary(const std::string& path) {
    std::ofstream f(path, std::ios::binary);

    // Write primitives:
    int32_t  count = 3;
    float    value = 3.14f;
    uint64_t id    = 99999999ULL;

    f.write(reinterpret_cast<const char*>(&count), sizeof(count));
    f.write(reinterpret_cast<const char*>(&value), sizeof(value));
    f.write(reinterpret_cast<const char*>(&id),    sizeof(id));

    // Write array of structs:
    Vec3 vertices[] = {{1,0,0}, {0,1,0}, {0,0,1}};
    f.write(reinterpret_cast<const char*>(vertices), sizeof(vertices));
}

// ── Read binary ───────────────────────────────────────────────
void readBinary(const std::string& path) {
    std::ifstream f(path, std::ios::binary);
    if (!f) throw std::runtime_error("Cannot open");

    int32_t  count;
    float    value;
    uint64_t id;

    f.read(reinterpret_cast<char*>(&count), sizeof(count));
    f.read(reinterpret_cast<char*>(&value), sizeof(value));
    f.read(reinterpret_cast<char*>(&id),    sizeof(id));

    Vec3 vertices[3];
    f.read(reinterpret_cast<char*>(vertices), sizeof(vertices));

    std::cout << "count=" << count << " value=" << value
              << " id=" << id << "\\n";
}`,
    },
    {
        label: "Serialisation",
        code: `#include <fstream>
#include <string>
#include <cstdint>
#include <vector>

// ── Serialise: convert object to byte stream ──────────────────
// Challenge: std::string has variable length → must write length first

struct Person {
    std::string name;
    int32_t     age;
    double      salary;
};

void serialize(std::ofstream& f, const Person& p) {
    // Write string: length prefix + data
    uint32_t nameLen = static_cast<uint32_t>(p.name.size());
    f.write(reinterpret_cast<const char*>(&nameLen), sizeof(nameLen));
    f.write(p.name.data(), nameLen);

    // Write fixed-size fields:
    f.write(reinterpret_cast<const char*>(&p.age),    sizeof(p.age));
    f.write(reinterpret_cast<const char*>(&p.salary), sizeof(p.salary));
}

Person deserialize(std::ifstream& f) {
    Person p;

    // Read string: length then data
    uint32_t nameLen;
    f.read(reinterpret_cast<char*>(&nameLen), sizeof(nameLen));
    p.name.resize(nameLen);
    f.read(p.name.data(), nameLen);

    // Read fixed-size fields:
    f.read(reinterpret_cast<char*>(&p.age),    sizeof(p.age));
    f.read(reinterpret_cast<char*>(&p.salary), sizeof(p.salary));

    return p;
}

// ── Serialise a vector of objects ────────────────────────────
void savePersons(const std::string& path, const std::vector<Person>& people) {
    std::ofstream f(path, std::ios::binary);
    uint32_t count = static_cast<uint32_t>(people.size());
    f.write(reinterpret_cast<const char*>(&count), sizeof(count));
    for (const auto& p : people) serialize(f, p);
}`,
    },
    {
        label: "File Header / Magic Bytes",
        code: `#include <fstream>
#include <cstdint>
#include <stdexcept>

// ── File headers identify file format and version ─────────────
// Real-world examples:
//   PNG:  137 80 78 71 13 10 26 10 (\\x89PNG\\r\\n\\x1a\\n)
//   ZIP:  50 4B 03 04 (PK..)
//   ELF:  7F 45 4C 46 (\\x7fELF)
//   PDF:  25 50 44 46 (%PDF)

struct FileHeader {
    char     magic[4];     // identifies file format: "MYDB"
    uint32_t version;      // format version number
    uint32_t recordCount;  // number of records
    uint32_t flags;        // feature flags
};

void writeWithHeader(const std::string& path, uint32_t count) {
    std::ofstream f(path, std::ios::binary);

    FileHeader hdr{};
    hdr.magic[0]='M'; hdr.magic[1]='Y';
    hdr.magic[2]='D'; hdr.magic[3]='B';
    hdr.version     = 1;
    hdr.recordCount = count;
    hdr.flags       = 0;

    f.write(reinterpret_cast<const char*>(&hdr), sizeof(hdr));
    // ... write records ...
}

FileHeader readAndValidateHeader(const std::string& path) {
    std::ifstream f(path, std::ios::binary);
    if (!f) throw std::runtime_error("Cannot open: " + path);

    FileHeader hdr;
    f.read(reinterpret_cast<char*>(&hdr), sizeof(hdr));
    if (!f) throw std::runtime_error("File too small to contain header");

    if (hdr.magic[0]!='M'||hdr.magic[1]!='Y'||
        hdr.magic[2]!='D'||hdr.magic[3]!='B')
        throw std::runtime_error("Invalid file format (wrong magic bytes)");

    if (hdr.version != 1)
        throw std::runtime_error("Unsupported version: " +
                                  std::to_string(hdr.version));
    return hdr;
}`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 6: std::filesystem (C++17)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const fsTabs = [
    {
        label: "Path Operations",
        code: `#include <filesystem>
namespace fs = std::filesystem;

// ── fs::path — portable path manipulation ────────────────────
fs::path p1 = "/home/user/docs/file.txt";
fs::path p2 = "C:\\\\Users\\\\Alice\\\\file.txt";  // Windows
fs::path p3 = fs::current_path();           // current working directory

// ── Path components ───────────────────────────────────────────
p1.filename();        // "file.txt"
p1.stem();            // "file"         (no extension)
p1.extension();       // ".txt"
p1.parent_path();     // "/home/user/docs"
p1.root_path();       // "/"
p1.is_absolute();     // true
p1.is_relative();     // false

// ── Path construction ─────────────────────────────────────────
fs::path base = "/home/user";
fs::path full = base / "docs" / "file.txt";  // operator/ joins paths
// full = "/home/user/docs/file.txt"

fs::path changed = full;
changed.replace_extension(".csv");  // "/home/user/docs/file.csv"

// ── Path conversion ───────────────────────────────────────────
std::string s    = p1.string();          // platform native string
std::string u8   = p1.u8string();        // UTF-8 encoded
const char* cstr = p1.c_str();           // C string

// ── Normalise ─────────────────────────────────────────────────
fs::path messy = "/home/user/../user/./docs//file.txt";
fs::path clean = messy.lexically_normal();  // "/home/user/docs/file.txt"`,
    },
    {
        label: "File System Operations",
        code: `#include <filesystem>
namespace fs = std::filesystem;

// ── Existence & type checks ───────────────────────────────────
fs::path p = "myfile.txt";
fs::exists(p);              // true if path exists (any type)
fs::is_regular_file(p);     // true if it's a normal file
fs::is_directory(p);        // true if it's a directory
fs::is_symlink(p);          // true if symbolic link

// ── File metadata ─────────────────────────────────────────────
fs::file_size(p);           // file size in bytes
fs::last_write_time(p);     // modification time (file_time_type)

// ── Create / Remove ───────────────────────────────────────────
fs::create_directory("newdir");           // create one level
fs::create_directories("a/b/c/d");        // create nested (like mkdir -p)
fs::remove("file.txt");                   // remove file or empty dir
fs::remove_all("mydir");                  // remove dir and all contents

// ── Copy / Rename / Move ──────────────────────────────────────
fs::copy_file("src.txt", "dst.txt");
fs::copy_file("src.txt", "dst.txt",
    fs::copy_options::overwrite_existing); // overwrite if exists
fs::rename("old.txt", "new.txt");          // rename or move file
fs::copy("srcdir", "dstdir",              // copy entire directory tree
    fs::copy_options::recursive);

// ── Temporary files ───────────────────────────────────────────
fs::path tmp = fs::temp_directory_path();       // system temp dir
fs::path tmpFile = tmp / "myapp_XXXXXX.tmp";    // template for unique name`,
    },
    {
        label: "Directory Iteration",
        code: `#include <filesystem>
#include <iostream>
#include <vector>
namespace fs = std::filesystem;

// ── List directory contents ───────────────────────────────────
void listDirectory(const fs::path& dir) {
    for (const auto& entry : fs::directory_iterator(dir)) {
        std::cout << entry.path().filename().string();
        if (entry.is_directory())     std::cout << "/";
        if (entry.is_regular_file())  std::cout << " (" << entry.file_size() << " bytes)";
        std::cout << "\\n";
    }
}

// ── Recursive directory walk ──────────────────────────────────
void walkTree(const fs::path& root) {
    for (const auto& entry : fs::recursive_directory_iterator(root)) {
        // entry.depth() gives nesting level
        std::string indent(entry.depth() * 2, ' ');
        std::cout << indent << entry.path().filename().string() << "\\n";
    }
}

// ── Find all files with specific extension ────────────────────
std::vector<fs::path> findByExtension(
        const fs::path& root, const std::string& ext) {
    std::vector<fs::path> results;
    for (const auto& entry : fs::recursive_directory_iterator(root)) {
        if (entry.is_regular_file() && entry.path().extension() == ext)
            results.push_back(entry.path());
    }
    return results;
}

// Usage: find all .cpp files:
auto cppFiles = findByExtension("/home/user/project", ".cpp");

// ── Directory size (sum of all files) ─────────────────────────
uintmax_t dirSize(const fs::path& dir) {
    uintmax_t total = 0;
    for (const auto& e : fs::recursive_directory_iterator(dir))
        if (e.is_regular_file()) total += e.file_size();
    return total;
}`,
    },
];

// Open mode table
const openModes = [
    { mode: "ios::in",     effect: "Open for reading",                  ifstream: "Default", ofstream: "—",       fstream: "Explicit" },
    { mode: "ios::out",    effect: "Open for writing (truncates!)",      ifstream: "—",       ofstream: "Default", fstream: "Explicit" },
    { mode: "ios::app",    effect: "Append — always write at end",       ifstream: "—",       ofstream: "Add",     fstream: "Add" },
    { mode: "ios::ate",    effect: "Seek to end on open",                ifstream: "Opt",     ofstream: "Opt",     fstream: "Opt" },
    { mode: "ios::trunc",  effect: "Truncate to zero on open",           ifstream: "—",       ofstream: "With out",fstream: "Opt" },
    { mode: "ios::binary", effect: "Binary mode — no \\n translation",   ifstream: "Opt",     ofstream: "Opt",     fstream: "Opt" },
];

// Cheatsheet
const cheatBlocks = [
    {
        title: "Reading", color: "#58a6ff",
        items: [
            "✅ Use getline() for full lines (preserves spaces)",
            "✅ Use >> to read tokens (skips whitespace)",
            "✅ Use istringstream to parse a line's fields",
            "✅ Check !file before reading to detect open failure",
            "✅ Loop with getline() or >> exits on EOF automatically",
            "❌ Don't mix getline and >> without clearing '\\n' first",
            "❌ Don't use eof() as loop condition (double-reads last line)",
        ],
    },
    {
        title: "Writing", color: "#7ee787",
        items: [
            "✅ ofstream truncates by default — use ios::app to append",
            "✅ flush() before closing critical data",
            "✅ Write to temp file then rename for atomic write",
            "✅ Accept std::ostream& in functions (testable with cout)",
            "✅ Use setw/setprecision for formatted output",
            "❌ Don't ignore write errors — check !file after writes",
            "❌ Don't write std::string to binary — serialise length first",
        ],
    },
    {
        title: "Binary Files", color: "#f0883e",
        items: [
            "✅ Always use ios::binary for non-text files",
            "✅ Use read/write, NOT >> and << for binary",
            "✅ reinterpret_cast<char*>(&obj) to read/write structs",
            "✅ Write magic bytes + version in file header",
            "✅ Use fixed-size integers (int32_t, uint64_t) for portability",
            "❌ Don't serialize std::string directly (variable length!)",
            "❌ Don't assume struct sizes — platform differences in padding",
        ],
    },
    {
        title: "std::filesystem", color: "#a855f7",
        items: [
            "✅ Use fs::path for all path manipulation",
            "✅ Check fs::exists() before opening files",
            "✅ Use create_directories() for nested dirs (not create_directory)",
            "✅ Use remove_all() to delete directory trees",
            "✅ Use fs::copy_options::overwrite_existing explicitly",
            "❌ Don't concatenate paths with +, use / operator",
            "❌ Don't hardcode path separators — use fs::path",
        ],
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function CppFileHandling() {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [openTab,   setOpenTab]   = useState(0);
    const [readTab,   setReadTab]   = useState(0);
    const [writeTab,  setWriteTab]  = useState(0);
    const [seekTab,   setSeekTab]   = useState(0);
    const [binTab,    setBinTab]    = useState(0);
    const [fsTab,     setFsTab]     = useState(0);

    return (
        <PageWrapper>

            {/* ── Page Header ── */}
            <Box sx={{ textAlign: "center", mb: 5, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{
                    fontWeight: 800, color: "goldenrod", display: "inline-block",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                    border: "1px solid rgba(218,165,32,0.25)", letterSpacing: "0.04em",
                }}>
                    C++ FILE HANDLING
                </Typography>
                <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.45)", fontSize: "0.88rem" }}>
                    ifstream · ofstream · fstream · Binary I/O · seekg/seekp · std::filesystem
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2 }}>
                    {["Open & Close","Reading","Writing","Random Access","Binary Files","std::filesystem"].map(t => (
                        <Chip key={t} label={t} size="small" sx={{
                            bgcolor: "rgba(218,165,32,0.1)", color: "rgba(218,165,32,0.85)",
                            border: "1px solid rgba(218,165,32,0.25)", fontSize: "0.72rem",
                        }} />
                    ))}
                </Box>
            </Box>

            {/* ── Intro Banner ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ bgcolor: "rgba(218,165,32,0.06)", border: "1px solid rgba(218,165,32,0.2)", borderRadius: "14px", p: "18px 24px", display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "1.4rem", flexShrink: 0 }}>💡</Typography>
                    <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.85, color: "rgba(255,255,255,0.8)" }}>
                        C++ file I/O is built on the <strong style={{ color: "goldenrod" }}>fstream</strong> stream
                        hierarchy — the same <code style={{ color: "goldenrod" }}>&lt;&lt;</code> and{" "}
                        <code style={{ color: "goldenrod" }}>&gt;&gt;</code> operators you use with{" "}
                        <code style={{ color: "goldenrod" }}>cout/cin</code> work with files. Combined with
                        RAII, files close automatically. <strong style={{ color: "goldenrod" }}>std::filesystem</strong>{" "}
                        (C++17) adds portable path and directory operations on top.
                    </Typography>
                </Box>
            </Box>

            {/* ── Stream class overview ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 2 }}>
                    {[
                        { cls: "std::ifstream", icon: "📂", color: "#58a6ff", header: "<fstream>", desc: "Input only. Open, read, close. Default mode: ios::in.", ops: ">> · getline · read · seekg · tellg" },
                        { cls: "std::ofstream", icon: "💾", color: "#7ee787", header: "<fstream>", desc: "Output only. Creates or truncates. Default mode: ios::out.", ops: "<< · put · write · seekp · tellp" },
                        { cls: "std::fstream",  icon: "🔄", color: "#f0883e", header: "<fstream>", desc: "Read + write. Must specify ios::in | ios::out explicitly.", ops: "All of the above combined" },
                    ].map((s, i) => (
                        <Box key={i} sx={{ bgcolor: "rgba(26,14,52,0.9)", border: `1px solid ${s.color}44`, borderTop: `3px solid ${s.color}`, borderRadius: "14px", p: "16px 18px" }}>
                            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 1 }}>
                                <Typography sx={{ fontSize: "1.4rem" }}>{s.icon}</Typography>
                                <Box>
                                    <Typography sx={{ fontFamily: "monospace", fontWeight: 800, color: s.color, fontSize: "0.9rem" }}>{s.cls}</Typography>
                                    <PillChip label={s.header} color={s.color} />
                                </Box>
                            </Box>
                            <Typography sx={{ fontSize: "0.8rem", opacity: 0.75, mb: 0.75, lineHeight: 1.6 }}>{s.desc}</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: "0.74rem", color: "rgba(255,255,255,0.4)" }}>{s.ops}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>

                    {/* ════════════════════════════════════════════════════
              SECTION 1 — OPENING & CLOSING FILES
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            1. Opening &amp; Closing Files
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Files can be opened in the constructor or with <code style={{ color: "goldenrod" }}>open()</code>.
                            Thanks to RAII, the destructor closes the file automatically — you rarely need to
                            call <code style={{ color: "goldenrod" }}>close()</code> explicitly. Always check that the
                            file opened successfully before reading or writing.
                        </Typography>

                        <ConceptCard title="RAII Lifecycle" color="#f0883e">
                            <CodeBlock>{`//  {                                ← new scope
//      std::ifstream f("data.txt");  ← constructor opens file
//
//      f >> data;                   ← use the file
//      processData(data);
//
//      throw SomeException();       ← even on exception!
//  }                                ← f destructor closes file
//                                      file ALWAYS closed — no leak
//
//  This is why std::fstream is preferred over C's fopen/fclose.
//  With RAII you can never forget to close a file.`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={openTab} onChange={(_, v) => setOpenTab(v)} tabs={openTabs.map(t => t.label)} />
                        <CodeBlock>{openTabs[openTab].code}</CodeBlock>

                        {/* Open modes table */}
                        <SectionHeading icon={<AiFillBulb size={18} />} title="File Open Modes" subtitle="Combine with | operator: ios::in | ios::binary" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Mode Flag", "Effect", "ifstream", "ofstream", "fstream"]} cols="130px 1fr 90px 90px 90px" />}
                            {openModes.map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.85rem", mb: 0.4 }}>{row.mode}</Typography>
                                        <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.65)", mb: 0.4 }}>{row.effect}</Typography>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            {[["ifstream", row.ifstream], ["ofstream", row.ofstream], ["fstream", row.fstream]].map(([k, v]) => (
                                                <Box key={k} sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                                                    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.35)" }}>{k}:</Typography>
                                                    <Typography sx={{ fontSize: "0.7rem", color: v === "—" ? "#f85149" : "#56d364" }}>{v}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "130px 1fr 90px 90px 90px", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", fontSize: "0.82rem", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", color: "goldenrod", fontWeight: 600, fontSize: "0.8rem" }}>{row.mode}</Box>
                                        <Box sx={{ opacity: 0.75 }}>{row.effect}</Box>
                                        {[row.ifstream, row.ofstream, row.fstream].map((v, j) => (
                                            <Box key={j} sx={{ color: v === "—" ? "#f85149" : v === "Default" ? "#fbbf24" : "#56d364", fontSize: "0.8rem" }}>{v}</Box>
                                        ))}
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 2 — READING FILES
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            2. Reading Files
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            C++ offers several ways to read: <code style={{ color: "goldenrod" }}>getline()</code> for
                            full lines, <code style={{ color: "goldenrod" }}>{">>"}</code> for whitespace-delimited
                            tokens, <code style={{ color: "goldenrod" }}>read()</code> for raw bytes.
                            Choosing the right method depends on the file format.
                        </Typography>

                        <ConceptCard title="getline() vs >> — Key Differences" color="#58a6ff">
                            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                                {[
                                    { title: "std::getline(file, line)", color: "#58a6ff", points: ["Reads until '\\n' (or specified delimiter)", "Preserves spaces within the line", "'\\n' consumed but NOT stored in line", "Best for: line-by-line text files, CSV"] },
                                    { title: "file >> variable",          color: "#7ee787", points: ["Skips leading whitespace automatically", "Reads until next whitespace", "Best for: space/tab-separated numbers or words", "Mixes with getline — but clear '\\n' first!"] },
                                ].map((item, j) => (
                                    <Box key={j} sx={{ bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${item.color}33`, borderRadius: "10px", p: "12px 14px" }}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: item.color, fontSize: "0.82rem", mb: 0.75 }}>{item.title}</Typography>
                                        {item.points.map((pt, k) => (
                                            <Typography key={k} sx={{ fontSize: "0.79rem", opacity: 0.72, lineHeight: 1.65 }}>• {pt}</Typography>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        </ConceptCard>

                        <StyledTabs value={readTab} onChange={(_, v) => setReadTab(v)} tabs={readTabs.map(t => t.label)} />
                        <CodeBlock>{readTabs[readTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 3 — WRITING FILES
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            3. Writing Files
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Writing uses the same <code style={{ color: "goldenrod" }}>&lt;&lt;</code> operator as{" "}
                            <code style={{ color: "goldenrod" }}>std::cout</code>. Functions that accept
                            <code style={{ color: "goldenrod" }}>std::ostream&</code> work with both files and console.
                            For critical data, always write atomically to prevent corruption.
                        </Typography>

                        <StyledTabs value={writeTab} onChange={(_, v) => setWriteTab(v)} tabs={writeTabs.map(t => t.label)} />
                        <CodeBlock>{writeTabs[writeTab].code}</CodeBlock>

                        {/* iomanip formatting */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🎨</span>} title="Output Formatting with &lt;iomanip&gt;" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Manipulator", "Effect", "Example Output"]} />}
                            {[
                                { m: "std::setw(n)",           e: "Set field width to n chars",          x: 'setw(10) << "Hi" → "        Hi"' },
                                { m: "std::left / right",       e: "Left/right align within field width", x: 'left << setw(10) << "Hi" → "Hi        "' },
                                { m: "std::setfill(c)",         e: "Fill character (default: space)",     x: 'setfill(\'0\') << setw(5) << 7 → "00007"' },
                                { m: "std::fixed",              e: "Fixed decimal notation",              x: 'fixed << 3.14159 → "3.141590"' },
                                { m: "std::setprecision(n)",    e: "Digits after decimal (with fixed)",   x: 'fixed << setprecision(2) << 3.14159 → "3.14"' },
                                { m: "std::scientific",         e: "Scientific notation",                 x: 'scientific << 12345.6 → "1.234560e+04"' },
                                { m: "std::hex / oct / dec",    e: "Integer base (hex, octal, decimal)",  x: 'hex << 255 → "ff"' },
                                { m: "std::boolalpha",          e: "Print true/false instead of 1/0",     x: 'boolalpha << true → "true"' },
                            ].map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontFamily: "monospace", color: "goldenrod", fontWeight: 700, fontSize: "0.82rem", mb: 0.4 }}>{row.m}</Typography>
                                        <Typography sx={{ fontSize: "0.79rem", color: "rgba(255,255,255,0.55)", mb: 0.4 }}>{row.e}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.75rem" }}>{row.x}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", fontSize: "0.82rem", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", color: "goldenrod", fontSize: "0.8rem", fontWeight: 600 }}>{row.m}</Box>
                                        <Box sx={{ opacity: 0.72 }}>{row.e}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem" }}>{row.x}</Box>
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 4 — RANDOM ACCESS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            4. Random Access — seekg / seekp
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Sequential I/O reads from start to end. <strong>Random access</strong> lets you jump
                            to any byte offset with <code style={{ color: "goldenrod" }}>seekg</code> (read) and{" "}
                            <code style={{ color: "goldenrod" }}>seekp</code> (write). This enables O(1) record
                            lookup in fixed-length binary databases.
                        </Typography>

                        <StyledTabs value={seekTab} onChange={(_, v) => setSeekTab(v)} tabs={seekTabs.map(t => t.label)} />
                        <CodeBlock>{seekTabs[seekTab].code}</CodeBlock>

                        {/* Seek quick reference */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>📍</span>} title="seek / tell Quick Reference" />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {[
                                { call: "seekg(n)",                   color: "#58a6ff", desc: "Move read pointer to byte n from beginning",                     example: "file.seekg(100);" },
                                { call: "seekg(n, ios::end)",          color: "#58a6ff", desc: "Move read pointer to n bytes before end (n is usually negative)",example: "file.seekg(-4, ios::end);" },
                                { call: "seekg(n, ios::cur)",          color: "#58a6ff", desc: "Move read pointer n bytes forward/backward from current pos",    example: "file.seekg(+20, ios::cur);" },
                                { call: "tellg()",                     color: "#fbbf24", desc: "Return current read position as byte offset from beginning",     example: "auto pos = file.tellg();" },
                                { call: "seekp(n)",                    color: "#7ee787", desc: "Move write pointer to byte n from beginning",                    example: "file.seekp(0);" },
                                { call: "seekp(0, ios::end)",           color: "#7ee787", desc: "Move write pointer to end (for appending without ios::app)",    example: "file.seekp(0, ios::end);" },
                                { call: "tellp()",                     color: "#fbbf24", desc: "Return current write position as byte offset",                   example: "auto pos = file.tellp();" },
                            ].map((row, i) => (
                                <Box key={i} sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1.5fr 1fr", gap: "12px", bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${row.color}22`, borderLeft: `3px solid ${row.color}`, borderRadius: "12px", px: 2, py: "10px", alignItems: "center" }}>
                                    <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: row.color, fontSize: "0.81rem" }}>{row.call}</Typography>
                                    <Typography sx={{ fontSize: "0.8rem", opacity: 0.78, lineHeight: 1.6 }}>{row.desc}</Typography>
                                    <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.78rem" }}>{row.example}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 5 — BINARY FILES
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            5. Binary Files
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Binary mode stores data as raw bytes — numbers take their native size (4 bytes for
                            int), no newline translation occurs, and you use{" "}
                            <code style={{ color: "goldenrod" }}>read()</code>/<code style={{ color: "goldenrod" }}>write()</code>
                            instead of <code style={{ color: "goldenrod" }}>&gt;&gt;</code>/<code style={{ color: "goldenrod" }}>&lt;&lt;</code>.
                            Essential for images, audio, databases, and protocols.
                        </Typography>

                        <ConceptCard title="Text vs Binary — Space & Speed" color="#f0883e">
                            <CodeBlock>{`// Storing int 12345:
//   Text mode:   writes "12345\\n"  = 6 bytes   (human-readable, bigger)
//   Binary mode: writes 0x39 0x30 0x00 0x00 = 4 bytes (compact, exact)
//
// Storing double 3.14159265358979:
//   Text mode:   "3.14159265358979\\n" = 18 bytes   (precision may be lost!)
//   Binary mode: 8 bytes exactly     (IEEE 754 — no precision loss)
//
// ── When to use binary ────────────────────────────────────────
//   ✅ Images (PNG, JPEG, BMP)
//   ✅ Audio (WAV, MP3 headers)
//   ✅ Custom databases / record files
//   ✅ Network protocol packets
//   ✅ Preserving exact floating-point values
//   ❌ Config files, logs, CSVs — use text (human-readable)`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={binTab} onChange={(_, v) => setBinTab(v)} tabs={binaryTabs.map(t => t.label)} />
                        <CodeBlock>{binaryTabs[binTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 6 — std::filesystem (C++17)
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            6. std::filesystem (C++17)
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <code style={{ color: "goldenrod" }}>std::filesystem</code> provides portable file
                            system operations — path manipulation, directory creation, iteration, copying, and
                            renaming — without platform-specific code. Compile with{" "}
                            <code style={{ color: "goldenrod" }}>-std=c++17</code> and link{" "}
                            <code style={{ color: "goldenrod" }}>-lstdc++fs</code> on older GCC.
                        </Typography>

                        <StyledTabs value={fsTab} onChange={(_, v) => setFsTab(v)} tabs={fsTabs.map(t => t.label)} />
                        <CodeBlock>{fsTabs[fsTab].code}</CodeBlock>

                        {/* Key functions */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🗂️</span>} title="Key std::filesystem Functions" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Function", "Returns", "Description"]} cols="1.5fr 100px 2fr" />}
                            {[
                                { fn: "fs::exists(p)",                  ret: "bool",        desc: "True if path exists (file, dir, symlink)" },
                                { fn: "fs::is_regular_file(p)",          ret: "bool",        desc: "True if path is a normal file" },
                                { fn: "fs::is_directory(p)",             ret: "bool",        desc: "True if path is a directory" },
                                { fn: "fs::file_size(p)",                ret: "uintmax_t",   desc: "File size in bytes. Throws if not regular file" },
                                { fn: "fs::create_directories(p)",       ret: "bool",        desc: "Create all intermediate directories (mkdir -p)" },
                                { fn: "fs::remove(p)",                   ret: "bool",        desc: "Remove file or empty directory" },
                                { fn: "fs::remove_all(p)",               ret: "uintmax_t",   desc: "Remove directory and all contents recursively" },
                                { fn: "fs::copy_file(src, dst, opts)",   ret: "bool",        desc: "Copy file. opts: overwrite_existing etc." },
                                { fn: "fs::rename(old, new)",            ret: "void",        desc: "Rename or move file/directory" },
                                { fn: "fs::current_path()",              ret: "fs::path",    desc: "Return current working directory" },
                                { fn: "fs::temp_directory_path()",       ret: "fs::path",    desc: "Return system temporary directory" },
                                { fn: "fs::directory_iterator(p)",       ret: "iterator",    desc: "Iterate contents of directory (non-recursive)" },
                                { fn: "fs::recursive_directory_iterator(p)", ret: "iterator",desc: "Walk entire directory tree depth-first" },
                            ].map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontFamily: "monospace", color: "goldenrod", fontWeight: 700, fontSize: "0.8rem", mb: 0.4 }}>{row.fn}</Typography>
                                        <Box sx={{ display: "flex", gap: 1, mb: 0.4 }}><PillChip label={row.ret} color="#a855f7" /></Box>
                                        <Typography sx={{ fontSize: "0.79rem", color: "rgba(255,255,255,0.55)" }}>{row.desc}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1.5fr 100px 2fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", fontSize: "0.82rem", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", color: "goldenrod", fontWeight: 600, fontSize: "0.79rem" }}>{row.fn}</Box>
                                        <PillChip label={row.ret} color="#a855f7" />
                                        <Box sx={{ opacity: 0.72 }}>{row.desc}</Box>
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              CHEATSHEET
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={3}>
                            File Handling Cheatsheet
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 3, mb: 4 }}>
                            {cheatBlocks.map((block, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${block.color}33`, borderRadius: "14px", p: "18px 20px" }}>
                                    <Typography sx={{ fontWeight: 800, color: block.color, fontSize: "0.88rem", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{block.title}</Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.65 }}>
                                        {block.items.map((item, j) => (
                                            <Typography key={j} sx={{ fontSize: "0.81rem", opacity: 0.82, lineHeight: 1.6 }}>{item}</Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Syntax quick reference */}
                        <Typography sx={{ fontWeight: 800, color: "goldenrod", fontSize: "0.88rem", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Syntax Quick Reference
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                            {[
                                { title: "Read file line by line", color: "#58a6ff", code: `std::ifstream f("file.txt");
std::string line;
while (std::getline(f, line))
    process(line);` },
                                { title: "Write & append to file",  color: "#7ee787", code: `// Write (truncate):
std::ofstream f("out.txt");
f << "Hello" << "\\n";
// Append:
std::ofstream a("log.txt", std::ios::app);
a << "new line\\n";` },
                                { title: "Binary read / write",     color: "#f0883e", code: `int x = 42;
// Write:
std::ofstream f("b.bin", std::ios::binary);
f.write(reinterpret_cast<char*>(&x), sizeof(x));
// Read:
std::ifstream g("b.bin", std::ios::binary);
g.read(reinterpret_cast<char*>(&x), sizeof(x));` },
                                { title: "std::filesystem C++17",   color: "#a855f7", code: `namespace fs = std::filesystem;
fs::path p = "dir/file.txt";
fs::exists(p);          // check
fs::create_directories("a/b/c");
for (auto& e : fs::directory_iterator("."))
    std::cout << e.path().filename();` },
                            ].map((item, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${item.color}33`, borderRadius: "12px", overflow: "hidden" }}>
                                    <Box sx={{ bgcolor: `${item.color}12`, px: 2, py: 1, borderBottom: `1px solid ${item.color}22` }}>
                                        <Typography sx={{ fontWeight: 700, color: item.color, fontSize: "0.82rem" }}>{item.title}</Typography>
                                    </Box>
                                    <CodeBlock sx={{ borderRadius: 0, border: "none", fontSize: "0.79rem", p: "12px 16px", "&::before": { content: '""' } }}>
                                        {item.code}
                                    </CodeBlock>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                </Grid>
            </Grid>
        </PageWrapper>
    );
}
